import moment from "moment"
import React, { useEffect, useState, useRef } from "react"
import Chart from "react-apexcharts"
import { getWeekTooltipLabel } from "utils/dateUtil"

const CHART_COLORS = {
  Accidents: "#EF5350",
  Absence: "#F97316",
  Complaints: "#6C63FF",
  "CCP Issues": "#10B981",
}

// ── Sort Order cycling button ────────────────────────────────────────────────
const SORT_STATES = [
  { key: "chronological", icon: "⇅", label: "Sort", color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
  { key: "ascending", icon: "↑", label: "Low → High", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  { key: "descending", icon: "↓", label: "High → Low", color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
]

const SortButton = ({ sortOrder, onToggle }) => {
  const current = SORT_STATES.find(s => s.key === sortOrder) || SORT_STATES[0]
  return (
    <button
      onClick={onToggle}
      title={`Sort Order: ${current.label} — click to change`}
      style={{
        display: "flex", alignItems: "center", gap: "8px",
        background: current.bg, border: `1.5px solid ${current.border}`,
        borderRadius: "10px", padding: "5px 12px",
        cursor: "pointer", outline: "none",
        transition: "all 0.18s ease", whiteSpace: "nowrap",
        userSelect: "none", minWidth: "115px",
      }}
    >
      <span style={{ fontSize: "15px", lineHeight: 1, color: current.color, fontWeight: 700, minWidth: "16px", textAlign: "center" }}>
        {current.icon}
      </span>
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <span style={{ fontSize: "8px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", lineHeight: 1.1 }}>
          Sort Order
        </span>
        <span style={{ fontSize: "11px", fontWeight: 700, color: current.color, lineHeight: 1.3 }}>
          {current.label}
        </span>
      </span>
    </button>
  )
}

const ChartCard = ({
  title,
  data,
  previousData,
  valueKey,
  showPercentage = false,
  showComparison = false,
  lowerIsBetter = false,
  sortOrder: sortOrderProp,
  onSortOrderChange,
}) => {
  const [localSortOrder, setLocalSortOrder] = useState("chronological")
  const sortOrder = sortOrderProp !== undefined ? sortOrderProp : localSortOrder
  const setSortOrder = onSortOrderChange ?? setLocalSortOrder

  const handleToggle = () => {
    const next =
      sortOrder === "chronological" ? "ascending"
        : sortOrder === "ascending" ? "descending"
          : "chronological"
    setSortOrder(next)
  }

  const [chartData, setChartData] = useState(null)
  const prevDataRef = useRef(null)

  const color = CHART_COLORS[title] || "#6C63FF"
  const prevColor = "#94a3b8"

  useEffect(() => {
    const isSame =
      JSON.stringify(prevDataRef.current) ===
      JSON.stringify({ data, previousData, valueKey, sortOrder })
    if (isSame) return
    prevDataRef.current = { data, previousData, valueKey, sortOrder }

    if (!data?.length) {
      setChartData(null)
      return
    }

    const hasPrev = !!previousData?.length

    const weekSet = new Set()
    data.forEach(item => weekSet.add(moment(item.date).isoWeek()))
    if (hasPrev)
      previousData.forEach(item => weekSet.add(moment(item.date).isoWeek()))
    const allWeeks = Array.from(weekSet).sort((a, b) => a - b)

    const currentMap = {}
    data.forEach(item => {
      currentMap[moment(item.date).isoWeek()] = Number(item[valueKey] || 0)
    })
    const prevMap = {}
    if (hasPrev) {
      previousData.forEach(item => {
        prevMap[moment(item.date).isoWeek()] = Number(item[valueKey] || 0)
      })
    }

    const dateByWeek = {}
    data.forEach(item => {
      dateByWeek[moment(item.date).isoWeek()] = moment(item.date).format("DD-MM-YYYY")
    })
    const prevDateByWeek = {}
    if (hasPrev) {
      previousData.forEach(item => {
        prevDateByWeek[moment(item.date).isoWeek()] = moment(item.date).format("DD-MM-YYYY")
      })
    }

    const currentYear = moment(data[0].date).year()
    const previousYear = hasPrev
      ? moment(previousData[0].date).year()
      : currentYear - 1

    // ── Apply Sort Order ──────────────────────────────────────────────────
    let displayWeeks = [...allWeeks]
    if (sortOrder === "ascending") {
      displayWeeks.sort((a, b) => {
        const vA = currentMap[a] ?? 0
        const vB = currentMap[b] ?? 0
        return vA !== vB ? vA - vB : a - b
      })
    } else if (sortOrder === "descending") {
      displayWeeks.sort((a, b) => {
        const vA = currentMap[a] ?? 0
        const vB = currentMap[b] ?? 0
        return vA !== vB ? vB - vA : a - b
      })
    }

    const currentValues = displayWeeks.map(w => currentMap[w] ?? null)
    const prevValues = hasPrev ? displayWeeks.map(w => prevMap[w] ?? null) : []
    const categories = displayWeeks.map((_, i) => `${i + 1}`)
    const allDates = displayWeeks.map(w => dateByWeek[w] || null)
    const allPrevDates = displayWeeks.map(w => prevDateByWeek[w] || null)

    const fullSeries = hasPrev
      ? [
        { name: `${currentYear}`, data: currentValues },
        { name: `${previousYear}`, data: prevValues },
      ]
      : [{ name: title, data: currentValues }]

    setChartData({
      hasPrev,
      currentYear,
      previousYear,
      categories,
      allDates,
      allPrevDates,
      fullSeries,
    })
  }, [data, previousData, valueKey, title, sortOrder])

  const visibleCategories = chartData?.categories || []
  const visibleSeries = chartData?.fullSeries || []
  const visibleDates = chartData?.allDates || []
  const visiblePrevDates = chartData?.allPrevDates || []

  const hasPrev = chartData?.hasPrev || false
  const currentYear = chartData?.currentYear
  const previousYear = chartData?.previousYear

  const options = {
    chart: {
      type: "area",
      toolbar: { show: false },
      background: "transparent",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 400,
        animateGradually: { enabled: true, delay: 100 },
      },
      dropShadow: {
        enabled: !hasPrev,
        color,
        top: 4,
        blur: 10,
        opacity: 0.15,
      },
    },
    stroke: hasPrev
      ? { curve: "smooth", width: [3, 2], dashArray: [0, 6] }
      : { curve: "smooth", width: 3 },
    markers: {
      size: hasPrev ? [4, 3] : 4,
      colors: ["#fff"],
      strokeColors: hasPrev ? [color, prevColor] : [color],
      strokeWidth: hasPrev ? [3, 2] : 3,
      hover: { size: 7 },
    },
    fill: hasPrev
      ? {
        type: "gradient",
        gradient: {
          type: "vertical",
          opacityFrom: [0.35, 0],
          opacityTo: [0.02, 0],
          stops: [0, 100],
        },
      }
      : {
        type: "gradient",
        gradient: {
          type: "vertical",
          shadeIntensity: 0,
          gradientToColors: [color],
          inverseColors: false,
          opacityFrom: 0.4,
          opacityTo: 0.02,
          stops: [0, 95, 100],
        },
      },
    colors: hasPrev ? [color, prevColor] : [color],
    dataLabels: { enabled: false },
    xaxis: {
      categories: visibleCategories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: 0,
        style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 500 },
        formatter: val => `${val}`,
      },
      title: {
        text: "Week",
        style: { color: "#94a3b8", fontSize: "13px", fontWeight: 600 },
      },
    },
    yaxis: {
      title: {
        text: showPercentage ? `${title} (%)` : title,
        style: { color: "#94a3b8", fontSize: "11px", fontWeight: 600 },
      },
      labels: {
        style: { colors: "#94a3b8", fontSize: "13px" },
        formatter: val =>
          showPercentage ? `${Number(val).toFixed(0)}%` : val,
      },
      min: 0,
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 8, right: 16, bottom: 0, left: 8 },
    },
    tooltip: {
      theme: "dark",
      shared: true,
      intersect: false,
      style: { fontSize: "12px" },
      x: {
        formatter: (_val, { dataPointIndex }) => {
          const curLabel = getWeekTooltipLabel(
            dataPointIndex,
            visibleDates[dataPointIndex]
          )
          if (!hasPrev) return curLabel
          const prevLabel = getWeekTooltipLabel(
            dataPointIndex,
            visiblePrevDates[dataPointIndex]
          )
          return `${currentYear}: ${curLabel || "N/A"}<br/>${previousYear}: ${prevLabel || "N/A"
            }`
        },
      },
      y: {
        formatter: val =>
          val == null ? "N/A" : showPercentage ? `${val}%` : val,
      },
    },
    legend: hasPrev
      ? {
        show: true,
        position: "top",
        horizontalAlign: "right",
        fontSize: "11px",
        fontWeight: 600,
        markers: { radius: 3 },
      }
      : { show: false },
  }

  const total = visibleSeries?.[0]?.data?.reduce((a, b) => a + (b ?? 0), 0)

  // ── Current vs Previous Year comparison card ──
  // Previous year only counts ISO weeks that exist in the current year's data (like-for-like).
  let cmpCurrent = 0
  let cmpPrev = 0
  if (showComparison && data?.length && previousData?.length) {
    const currentWeeks = new Set()
    data.forEach(item => {
      currentWeeks.add(moment(item.date).isoWeek())
      cmpCurrent += Number(item[valueKey]) || 0
    })
    previousData.forEach(item => {
      if (currentWeeks.has(moment(item.date).isoWeek()))
        cmpPrev += Number(item[valueKey]) || 0
    })
  }
  const cmpDiff = cmpCurrent - cmpPrev
  const cmpPct = cmpPrev !== 0 ? (Math.abs(cmpDiff) / cmpPrev) * 100 : null
  const cmpGood = lowerIsBetter ? cmpDiff <= 0 : cmpDiff >= 0
  const cmpColor = cmpGood ? "#15803d" : "#be123c"
  const fmtCount = v => Number(v).toLocaleString("en-GB")
  const cmpRow = { display: "flex", alignItems: "center", gap: "6px" }
  const cmpLabel = { fontSize: "12px", color: "#374151", whiteSpace: "nowrap" }

  return (
    <div
      style={{
        background: "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
        borderRadius: "16px",
        border: `1px solid ${color}22`,
        boxShadow: `0 4px 24px ${color}14, 0 1px 4px rgba(0,0,0,0.04)`,
        position: "relative",
        overflow: "hidden",
        marginBottom: "1rem",
      }}
    >
      {/* Top accent bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${color}, ${color}88)`,
          borderRadius: "16px 16px 0 0",
        }}
      />

      <div style={{ padding: "20px 16px 8px" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "4px",
            padding: "0 8px",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: color,
                boxShadow: `0 0 8px ${color}88`,
              }}
            />
            <h4
              style={{
                margin: 0,
                fontSize: "13px",
                fontWeight: 700,
                color: "#1e293b",
                letterSpacing: "-0.2px",
              }}
            >
              {title}
            </h4>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            {/* Current vs Previous Year comparison */}
            {showComparison && hasPrev && (
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "6px 12px",
                  minWidth: "230px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <div style={cmpRow}>
                  <span
                    style={{ width: 8, height: 8, borderRadius: "50%", background: color }}
                  />
                  <span style={cmpLabel}>Current Year ({currentYear})</span>
                  <span style={{ marginLeft: "auto", fontSize: "13px", fontWeight: 700, color }}>
                    {fmtCount(cmpCurrent)}
                  </span>
                </div>
                <div style={{ ...cmpRow, borderTop: "1px dashed #e2e8f0", paddingTop: "4px" }}>
                  <span
                    style={{ width: 8, height: 8, borderRadius: "50%", background: prevColor }}
                  />
                  <span style={cmpLabel}>Previous Year ({previousYear})</span>
                  <span style={{ marginLeft: "auto", fontSize: "13px", fontWeight: 700, color: "#64748b" }}>
                    {fmtCount(cmpPrev)}
                  </span>
                </div>
                <div style={{ ...cmpRow, borderTop: "1px dashed #e2e8f0", paddingTop: "4px" }}>
                  <span style={cmpLabel}>Difference</span>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: "13px",
                      fontWeight: 700,
                      color: cmpColor,
                    }}
                  >
                    {cmpDiff >= 0 ? "+" : "-"}
                    {fmtCount(Math.abs(cmpDiff))}
                    {cmpPct !== null && (
                      <>
                        {" "}
                        {cmpDiff >= 0 ? "▲" : "▼"} {cmpPct.toFixed(1)}%
                      </>
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Sort Order cycling button */}
            <SortButton sortOrder={sortOrder} onToggle={handleToggle} />

            {/* Total badge */}
            <div
              style={{
                background: `${color}12`,
                border: `1px solid ${color}33`,
                borderRadius: "20px",
                padding: "3px 10px",
                fontSize: "11px",
                fontWeight: 700,
                color,
              }}
            >
              Total:{" "}
              {showPercentage
                ? total
                  ? `${total?.toFixed(1)}%`
                  : "0%"
                : total
                  ? total?.toLocaleString("en-GB")
                  : 0}
            </div>
          </div>
        </div>

        <Chart options={options} series={visibleSeries} type="area" height={350} />
      </div>
    </div>
  )
}

export default ChartCard
